import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { boxAssignmentsAPI, devicesAPI, depotsAPI, companiesAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { hasAtLeastPermission } from '../utils/moduleAccess';
import './BoxAssignments.css';

export default function BoxAssignments() {
  const currentUser = useAuthStore((state) => state.user);
  const permissions = useAuthStore((state) => state.permissions);
  const isAdminLike = currentUser?.user_type === 'gods_eye' || currentUser?.user_type === 'manager';
  const canEditAssignments = isAdminLike || ['cliente', 'distribuidor'].includes(currentUser?.user_type) || hasAtLeastPermission(permissions, 'device_assignments', 'edit');
  const [devices, setDevices] = useState([]);
  const [depots, setDepots] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [boxAssignments, setBoxAssignments] = useState([]); // Track assignments separately
  const [loading, setLoading] = useState(false);
  const [selectedBox, setSelectedBox] = useState(null);
  const [selectedBoxes, setSelectedBoxes] = useState([]);
  const [boxSearchTerm, setBoxSearchTerm] = useState('');
  const [selectedDepot, setSelectedDepot] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    // For operador, get company from their depot; for others, use their company_id or null
    if (currentUser?.user_type === 'operador') {
      // Will be set after depots are loaded
      return null;
    }
    return currentUser?.company_id || null;
  });

  useEffect(() => {
    if (currentUser?.user_type !== 'operador' && currentUser?.company_id && !selectedCompany) {
      setSelectedCompany(currentUser.company_id);
    }
  }, [currentUser, selectedCompany]);
  
  // Progress tracking state
  const [progressId, setProgressId] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [showProgress, setShowProgress] = useState(false);

  useEffect(() => {
    fetchDevices();
    fetchBoxAssignments(); // Fetch assignments
    fetchDepots();
    fetchCompanies();
  }, []);

  // For operador, set company from their depot
  useEffect(() => {
    if (currentUser?.user_type === 'operador' && currentUser?.depot_id && depots.length > 0) {
      const operadorDepot = depots.find(d => d.id === currentUser.depot_id);
      if (operadorDepot) {
        setSelectedCompany(operadorDepot.company_id);
        setSelectedDepot(operadorDepot.id);
      }
    }
  }, [depots, currentUser]);

  // Refresh selected box when devices change (to show updated device list)
  useEffect(() => {
    if (selectedBox) {
      // Find the box with the same name in the updated device list
      const devicesByBox = {};
      const filteredDevices = currentUser?.user_type === 'cliente' || currentUser?.user_type === 'verificador'
        ? devices.filter(d => d.company_id === currentUser?.company_id)
        : devices;

      filteredDevices.forEach(device => {
        if (!device.box) return;
        if (!devicesByBox[device.box]) {
          devicesByBox[device.box] = {
            name: device.box,
            devices: [],
            company_id: device.company_id,
          };
        }
        devicesByBox[device.box].devices.push(device);
      });

      const updatedBox = devicesByBox[selectedBox.name];
      if (updatedBox) {
        setSelectedBox(updatedBox);
      }
    }
  }, [devices, currentUser]);

  // Monitor progress stream for assignments
  useEffect(() => {
    if (!progressId) return;

    // Use relative path so nginx reverse proxy handles routing
    const eventSource = new EventSource(`/api/device-assignments/progress/${progressId}`);

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setProgressData(data);
        
        if (data.status === 'complete') {
          console.log('Progress complete, refreshing data...');
          eventSource.close();
          // Wait a moment for backend to ensure all data is committed, then refresh
          setTimeout(async () => {
            console.log('Fetching updated data after SSE completion');
            await fetchDevices();
            await fetchBoxAssignments();
            setProgressId(null);
            setTimeout(() => setShowProgress(false), 500);
          }, 500);
        }
      } catch (error) {
        console.error('Error parsing progress data:', error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('Progress stream error:', error);
      eventSource.close();
      setProgressId(null);
    };

    return () => eventSource.close();
  }, [progressId]);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      // Use public endpoint that filters by company/depot for non-gods_eye users
      const response = await devicesAPI.getPublic();
      const deviceList = response.data || [];
      setDevices(deviceList);
    } catch (error) {
      console.error('Error fetching devices:', error);
      console.error('Error response:', error.response);
      let errorMsg = 'Error fetching devices';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        errorMsg = typeof detail === 'string' ? detail : (detail.msg || JSON.stringify(detail));
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error(errorMsg);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoxAssignments = async () => {
    try {
      const response = await boxAssignmentsAPI.getAll();
      const assignmentsList = response.data || [];
      console.log('Fetched box assignments:', assignmentsList);
      setBoxAssignments(assignmentsList);
    } catch (error) {
      console.error('Error fetching box assignments:', error);
      setBoxAssignments([]);
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await depotsAPI.getPublic();
      const depotList = response.data || [];
      setDepots(depotList);
    } catch (error) {
      // Silently handle 403 errors (user doesn't have access)
      if (error.response?.status === 403) {
        console.log('Depots module not accessible');
        setDepots([]);
      } else {
        console.error('Error fetching depots:', error);
        setDepots([]);
      }
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companiesAPI.getPublic();
      const companyList = response.data || [];
      setCompanies(companyList);
    } catch (error) {
      // Silently handle 403 errors (user doesn't have access)
      if (error.response?.status === 403) {
        console.log('Companies module not accessible');
        setCompanies([]);
      } else {
        console.error('Error fetching companies:', error);
        setCompanies([]);
      }
    }
  };

  // Group devices by box
  const boxesByCompany = useMemo(() => {
    const boxMap = {};

    // Filter devices based on user type
    let filteredDevices = devices;
    
    if (currentUser?.user_type === 'cliente' || currentUser?.user_type === 'verificador' || currentUser?.user_type === 'distribuidor') {
      // Cliente, verificador, and distribuidor see only their company's devices
      filteredDevices = devices.filter(d => d.company_id === currentUser?.company_id);
    }
    // operador and gods_eye see all devices returned by API (already filtered by backend for operador)

    filteredDevices.forEach(device => {
      if (!device.box) return;
      if (!boxMap[device.box]) {
        boxMap[device.box] = {
          name: device.box,
          devices: [],
          company_id: device.company_id,
        };
      }
      boxMap[device.box].devices.push(device);
    });

    return Object.values(boxMap).sort((a, b) => (
      String(a.name).localeCompare(String(b.name), undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    ));
  }, [devices, currentUser]);

  const filteredBoxesByCompany = useMemo(() => {
    const search = boxSearchTerm.trim().toLowerCase();
    if (!search) return boxesByCompany;

    return boxesByCompany.filter((box) => (
      String(box.name).toLowerCase().includes(search)
    ));
  }, [boxesByCompany, boxSearchTerm]);

  const getBoxSelectionKey = (box) => `${box.company_id}:${box.name}`;

  const isBoxSelected = (box) => selectedBoxes.some(
    (selected) => selected.name === box.name && selected.company_id === box.company_id,
  );

  const toggleSelectedBox = (box) => {
    const key = getBoxSelectionKey(box);
    setSelectedBoxes((prev) => {
      const exists = prev.some((selected) => getBoxSelectionKey(selected) === key);
      if (exists) {
        return prev.filter((selected) => getBoxSelectionKey(selected) !== key);
      }

        if (prev.length > 0 && prev.some((selected) => selected.company_id !== box.company_id)) {
        toast.error('Select boxes from the same company to assign them together');
        return prev;
      }

        if (!selectedCompany) {
        setSelectedCompany(box.company_id);
      }

      return [...prev, { name: box.name, company_id: box.company_id }];
    });
  };

  // Get depots for selected company (or for operador's depot)
  const availableDepots = useMemo(() => {
    if (currentUser?.user_type === 'operador') {
      // operador only sees their assigned depot
      if (currentUser?.depot_id) {
        return depots.filter(d => d.id === currentUser.depot_id);
      }
      return [];
    }
    
    // cliente and gods_eye filter by selected company
    if (!selectedCompany) return [];
    return depots.filter(d => d.company_id === parseInt(selectedCompany));
  }, [depots, selectedCompany, currentUser]);

  const handleAssignBox = async (e) => {
    e.preventDefault();

    if (!canEditAssignments) {
      toast.error('You do not have permission to assign boxes');
      return;
    }

    const boxesToAssign = selectedBoxes.length > 0
      ? selectedBoxes
      : (selectedBox ? [selectedBox] : []);

    if (boxesToAssign.length === 0) {
      toast.error('Please select at least one box');
      return;
    }

    if (currentUser?.user_type === 'cliente' && !selectedDepot) {
      toast.error('Please select a depot');
      return;
    }

    if (currentUser?.user_type === 'distribuidor' && !selectedDepot) {
      toast.error('Please select a depot');
      return;
    }

    const companyIds = new Set(boxesToAssign.map((box) => box.company_id));
    if (companyIds.size > 1) {
      toast.error('Select boxes from the same company');
      return;
    }

    const companyId = boxesToAssign[0].company_id || selectedCompany;
    if (!companyId) {
      toast.error('Company is required');
      return;
    }

    const companyIdInt = parseInt(companyId);
    const depotIdInt = selectedDepot ? parseInt(selectedDepot) : null;

    try {
      setLoading(true);
      setShowProgress(true);
      
      const payload = {
        depot_id: depotIdInt,
      };

      // Show progress bar immediately
      setShowProgress(true);

      let operationId = null;

      if (boxesToAssign.length > 1) {
        const result = await boxAssignmentsAPI.bulkCreate({
          boxes: boxesToAssign.map((box) => box.name),
          company_id: companyIdInt,
          depot_id: depotIdInt,
        });
        operationId = result.data?.operation_id || null;
      } else {
        const singleBox = boxesToAssign[0];
        const response = await boxAssignmentsAPI.getAll();
        const boxAssignments = response.data?.filter(a => 
          a.box === singleBox.name && a.company_id === companyIdInt
        ) || [];

        const alreadyAssigned = boxAssignments.some(a => a.depot_id);

        if (alreadyAssigned && currentUser?.user_type !== 'gods_eye') {
          toast.error('This box is already assigned to a depot. Only administrators can modify assigned boxes.');
          setShowProgress(false);
          return;
        }

        if (boxAssignments.length === 0) {
          const createResult = await boxAssignmentsAPI.create({
            box: singleBox.name,
            company_id: companyIdInt,
            depot_id: depotIdInt,
          });
          operationId = createResult.data?.operation_id || null;
        } else {
          const updatePromises = boxAssignments.map(async (assignment, index) => {
            const result = await boxAssignmentsAPI.update(assignment.id, payload);
            if (index === 0 && result.data?.operation_id) {
              operationId = result.data.operation_id;
            }
            return result;
          });

          await Promise.all(updatePromises);
        }
      }

      if (operationId) {
        setProgressId(operationId);
      }
      
      const depotName = selectedDepot ? 
        depots.find(d => d.id === parseInt(selectedDepot))?.name || 'depot' : 
        'no depot';

      const boxLabel = boxesToAssign.length > 1
        ? `${boxesToAssign.length} boxes`
        : `Box "${boxesToAssign[0].name}"`;
      toast.success(`${boxLabel} assigned to ${depotName} successfully`);

      // Reset form - but keep selected box visible
      // Data will be refreshed when SSE progress completes
      setSelectedDepot(null);
      setSelectedBoxes([]);
    } catch (error) {
      setShowProgress(false);
      console.error('Error assigning box:', error);
      let errorMsg = 'Error assigning box';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Validation errors - extract messages
          errorMsg = detail.map(e => e.msg || e).join(', ');
        } else if (typeof detail === 'object') {
          // Single error object
          errorMsg = detail.msg || JSON.stringify(detail);
        } else {
          // String message
          errorMsg = detail;
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      
      toast.error(errorMsg);
    }
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return 'Unassigned';
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : `Company #${companyId}`;
  };

  const getDepotName = (depotId) => {
    if (!depotId) return '-';
    const depot = depots.find(d => d.id === depotId);
    return depot ? depot.name : `Depot #${depotId}`;
  };

  const getBoxDepot = (boxName, companyId) => {
    // First check if box has been assigned via DeviceAssignment
    const assignment = boxAssignments.find(a => 
      a.box === boxName && a.company_id === companyId
    );
    if (assignment?.depot_id) {
      return assignment.depot_id;
    }
    
    // Fallback: check if devices in this box have depot_id set
    const box = boxesByCompany.find(b => b.name === boxName && b.company_id === companyId);
    if (box && box.devices.length > 0) {
      const firstDeviceDepot = box.devices[0].depot_id;
      if (firstDeviceDepot) {
        return firstDeviceDepot;
      }
    }
    
    return null;
  };

  if (loading) {
    return <div className="container"><p>Loading boxes and devices...</p></div>;
  }

  return (
    <div className="container">
      <h1 className="page-title">Box Assignments</h1>

      <div className="box-assignments-layout">
        {/* Left side: List of boxes */}
        <div className="boxes-list">
          <div className="boxes-list-header">
            <h2>Available Boxes</h2>
            <span className="boxes-count">
              {filteredBoxesByCompany.length} / {boxesByCompany.length}
            </span>
          </div>
          <div className="box-search">
            <input
              type="search"
              value={boxSearchTerm}
              onChange={(e) => setBoxSearchTerm(e.target.value)}
              placeholder="Search by box"
              aria-label="Search by box"
            />
          </div>
          {boxesByCompany.length === 0 ? (
            <p className="no-boxes">No boxes available</p>
          ) : filteredBoxesByCompany.length === 0 ? (
            <p className="no-boxes">No boxes match your search</p>
          ) : (
            <div className="boxes-container">
              {filteredBoxesByCompany.map(box => (
                <div
                  key={getBoxSelectionKey(box)}
                  className={`box-item ${selectedBox?.name === box.name ? 'selected' : ''} ${isBoxSelected(box) ? 'bulk-selected' : ''}`}
                  onClick={() => {
                    setSelectedBox(box);
                    if (currentUser?.user_type !== 'gods_eye' || !selectedCompany) {
                      setSelectedCompany(box.company_id);
                    }
                    setSelectedDepot(null);
                  }}
                >
                  <div className="box-header">
                    <h3>{box.name}</h3>
                    <label className="box-select-toggle" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isBoxSelected(box)}
                        onChange={() => toggleSelectedBox(box)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span>Select</span>
                    </label>
                  </div>
                  <span className="device-count">{box.devices.length} seal{box.devices.length !== 1 ? 's' : ''}</span>
                  {currentUser?.user_type === 'gods_eye' && (
                    <div className="box-company">
                      <strong>Company:</strong> {getCompanyName(box.company_id)}
                    </div>
                  )}
                  <div className="box-depot">
                    <strong>Depot:</strong> <span style={{ fontWeight: 'bold', color: getBoxDepot(box.name, box.company_id) ? '#10b981' : '#ef4444' }}>
                      {getDepotName(getBoxDepot(box.name, box.company_id))}
                    </span>
                  </div>
                  <div className="box-status" style={{
                    marginTop: '8px',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '600',
                    display: 'inline-block',
                    backgroundColor: getBoxDepot(box.name, box.company_id) ? '#d1fae5' : '#fee2e2',
                    color: getBoxDepot(box.name, box.company_id) ? '#065f46' : '#991b1b',
                    border: `1px solid ${getBoxDepot(box.name, box.company_id) ? '#6ee7b7' : '#fca5a5'}`,
                  }}>
                    {getBoxDepot(box.name, box.company_id) ? `? ASSIGNED TO: ${getDepotName(getBoxDepot(box.name, box.company_id))}` : '? UNASSIGNED'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right side: Box details and assignment form */}
        <div className="box-details">
          {selectedBox ? (
            <>
              <div className="selected-box-header">
                <h2>Box: {selectedBox.name}</h2>
                <p className="device-count-info">{selectedBox.devices.length} seal{selectedBox.devices.length !== 1 ? 's' : ''}</p>
              </div>

              {/* List seals in the box */}
              <div className="devices-in-box">
                <h3>Seals in this box</h3>
                <div className="devices-list">
                  {selectedBox.devices.map(device => (
                    <div key={device.id} className="device-row">
                      <div className="device-info">
                        {/* Show different fields based on user type */}
                        {currentUser?.user_type === 'distribuidor' && (
                          <span className="device-meta">
                            Box: {device.box}
                          </span>
                        )}
                        {currentUser?.user_type === 'operador' && (
                          <span className="device-meta">
                            Bag: {device.bag}
                          </span>
                        )}
                        {currentUser?.user_type !== 'distribuidor' && currentUser?.user_type !== 'operador' && (
                          <span className="device-meta">
                            Bag: {device.bag} | Seal: {device.seal}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Assignment form */}
              <form onSubmit={handleAssignBox} className="assignment-form">
                {!canEditAssignments && (
                  <div style={{
                    marginBottom: '12px',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    backgroundColor: '#fef3c7',
                    border: '1px solid #facc15',
                    color: '#92400e',
                    fontSize: '13px',
                  }}>
                    You have view-only access to box assignments. Assigning boxes is disabled.
                  </div>
                )}
                {currentUser?.user_type === 'gods_eye' && (
                  <div className="form-group">
                    <label>Company</label>
                    <select
                      value={selectedCompany || ''}
                      onChange={(e) => {
                        setSelectedCompany(parseInt(e.target.value) || null);
                        setSelectedDepot(null);
                      }}
                      disabled={currentUser?.user_type !== 'gods_eye'}
                    >
                      <option value="">Select a company</option>
                      {companies.map(company => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedCompany && (
                  <div className="form-group">
                    <label>Depot</label>
                    <select
                      value={selectedDepot || ''}
                      onChange={(e) => setSelectedDepot(e.target.value ? parseInt(e.target.value) : null)}
                      disabled={currentUser?.user_type === 'operador' || !canEditAssignments}
                      required={currentUser?.user_type !== 'operador' && canEditAssignments}
                    >
                      <option value="">Select a depot</option>
                      {availableDepots.map(depot => (
                        <option key={depot.id} value={depot.id}>
                          {depot.name}
                        </option>
                      ))}
                    </select>
                    {currentUser?.user_type === 'operador' && (
                      <small style={{ color: '#666', marginTop: '5px', display: 'block' }}>
                        Your assigned depot
                      </small>
                    )}
                  </div>
                )}

                <div className="form-actions">
                    <button
                    type="submit"
                    className="btn btn-success"
                    disabled={!canEditAssignments}
                  >
                    {selectedBoxes.length > 1 ? 'Assign Selected Boxes to Depot' : 'Assign Box to Depot'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setSelectedBox(null);
                      setSelectedDepot(null);
                        setSelectedBoxes([]);
                      setSelectedCompany(currentUser?.company_id || null);
                    }}
                  >
                    Clear Selection
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="no-selection">
              <p>Select a box from the list to view its seals and assign it to a depot</p>
            </div>
          )}
        </div>
        
        {/* Progress Bar */}
        {showProgress && progressData && (
          <div className="progress-container">
            <div className="progress-header">
              <h4>Assignment Progress</h4>
              <span className="progress-status">{progressData.status === 'complete' ? '? Complete' : 'In Progress...'}</span>
            </div>
            <div className="progress-info">
              <span>{progressData.current} / {progressData.total} seals</span>
              <span className="progress-percentage">{progressData.percentage}%</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ 
                  width: `${progressData.percentage}%`,
                  backgroundColor: progressData.status === 'error' ? '#dc3545' : '#28a745'
                }}
              ></div>
            </div>
            {progressData.message && (
              <div className="progress-message">
                {progressData.message}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
